/**
 * CameraModelTags — the assigned-AI-model chips on a camera card's title bar.
 *
 * WHAT WAS WRONG WITH THEM
 * ------------------------
 * They were `text-[8px] uppercase tracking-wide` inside an `overflow-hidden` strip. Three
 * separate problems, all of which land on the same person trying to read them:
 *
 *   • 8px is below the size at which a name is read rather than recognised, and UPPERCASE
 *     removes the ascenders and descenders that make a word recognisable at a glance — so
 *     "PPE VIOLATION" and "PERSON COUNT" were two grey-blue smudges of similar length;
 *   • the strip clipped, so a chip that did not fit was cut through the middle of a letter.
 *     A tag sliced mid-glyph does not read as "there is more", it reads as broken rendering;
 *   • every assigned model got a chip, so a camera with four models produced four cut-off
 *     stubs in a 250px bar.
 *
 * WHAT IT DOES INSTEAD: readable type at the size the rest of the app uses for a chip, a
 * cap of two chips with a "+N" for the remainder (the tooltip and the title still carry the
 * full list, so nothing is lost), and per-chip truncation with an ellipsis — the chip decides
 * where a long name ends, not the edge of the bar.
 *
 * Shared because the local node's CameraLiveCard and the cloud's LiveVideoCard drew this
 * strip from two hand-written copies of the same JSX, which is how they came to disagree
 * about it. The DESTINATION differs between them (the cloud's routes are node-scoped) and
 * that is the one thing the host still decides, through `hrefFor`.
 */
import Tooltip from '../common/Tooltip';
import { modelSurfaceForName } from '../../constants/modelSurfaces';

/** Chips beyond this collapse into "+N". Two is what fits beside a camera name at 250px. */
const MAX_CHIPS = 2;

/** One chip style for every model, heatmap included — the destination differs, the badge shouldn't. */
const CHIP =
  'inline-block max-w-[7.5rem] flex-shrink-0 truncate rounded-md px-1.5 py-[1px] text-[10px] font-semibold ' +
  'bg-sky-100 text-sky-700 ring-1 ring-inset ring-sky-200 ' +
  'dark:bg-sky-900/40 dark:text-sky-300 dark:ring-sky-800';

export interface CameraModelTagsProps {
  /** Assigned model names, direct and inherited. */
  names: string[];
  /**
   * Where this chip should take the operator, or null when the model has no surface or the
   * role may not open it. The host owns this because the two apps' routes differ: a node's
   * own `/reports/…` versus the cloud's node-scoped paths.
   */
  hrefFor?: (name: string, surface: string) => string | null;
  /** Perform the navigation. Kept out of here so this file needs no router. */
  onOpen?: (href: string) => void;
  className?: string;
}

export default function CameraModelTags({ names, hrefFor, onOpen, className = '' }: CameraModelTagsProps) {
  if (names.length === 0) return null;

  const shown = names.slice(0, MAX_CHIPS);
  const hidden = names.slice(MAX_CHIPS);

  return (
    <Tooltip label={names.join(', ')} className={`min-w-0 flex-1 items-center justify-end ${className}`}>
      {/* justify-end: the chips sit against the card's right edge rather than crowding the
          camera name, which is what makes the bar readable as "name … models" instead of one
          run of text. */}
      <div className="flex min-w-0 items-center gap-1">
        {shown.map((name) => {
          const surface = modelSurfaceForName(name);
          const href = surface && hrefFor ? hrefFor(name, surface) : null;
          if (!href || !onOpen) {
            return (
              <span key={name} className={CHIP} title={name}>
                {name}
              </span>
            );
          }
          return (
            <button
              key={name}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpen(href);
              }}
              title={surface === 'heatmap' ? `View ${name} for this camera` : `Open ${name} report for this camera`}
              className={`${CHIP} cursor-pointer transition-shadow hover:ring-sky-400 dark:hover:ring-sky-600`}
            >
              {name}
            </button>
          );
        })}

        {hidden.length > 0 && (
          <span
            /* The remainder as a count, not as more clipped stubs. `title` spells it out for
               anyone who needs the names without hunting for the tooltip. */
            className="inline-block flex-shrink-0 rounded-md bg-muted px-1.5 py-[1px] text-[10px] font-semibold text-muted-foreground ring-1 ring-inset ring-border"
            title={hidden.join(', ')}
          >
            +{hidden.length}
          </span>
        )}
      </div>
    </Tooltip>
  );
}
